import { useEffect, useRef, useState } from 'react';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaArrowRight, FaGithub, FaLinkedin, FaAngleDown
} from 'react-icons/fa';
import SkillBar from '../components/SkillBar';
import './Home.css';
import './About.css'; // Import About styles for the merged content

// Helper function to get technology logo
const getTechLogo = (techName) => {
  const techLogos = {
    'LangGraph': 'Langgraph.png',
    'LangChain': 'Langchain.png',
    'Phidata': 'default-tech.svg',
    'LlamaIndex': 'default-tech.svg',
    'CrewAI': 'default-tech.svg',
    'TensorFlow': 'Tensorflow_logo.png',
    'PyTorch': 'pytorch_logo.png',
    'Python': 'python.png',
    'FastAPI': 'FastAPI_logo.png',
    'Django': 'Django.png',
    'Flask': 'flask.png',
    'React.js': 'React.png',
    'React': 'React.png',
    'JavaScript': 'JavaScript.png',
    'TypeScript': 'JavaScript.png',
    'HTML5': 'default-tech.svg',
    'CSS3': 'default-tech.svg',
    'Jupyter': 'Jupyter_logo.png',
    'HuggingFace': 'Huggingface_logo.svg',
    'Predictive Modeling': 'default-tech.svg',
    'Natural Language Processing': 'default-tech.svg',
    'Computer Vision': 'default-tech.svg',
    'MLOps & Model Deployment': 'default-tech.svg',
    'RESTful API Design': 'default-tech.svg',
    'Database Design': 'default-tech.svg',
    'Authentication & Security': 'default-tech.svg',
    'Microservices Architecture': 'default-tech.svg',
    'Redux': 'React.png',
    'Responsive Design': 'default-tech.svg',
    'UI/UX Principles': 'default-tech.svg'
  };

  // Check for exact match first
  if (techLogos[techName]) {
    return `/images/about/skills/${techLogos[techName]}`;
  }

  // Check for partial matches
  const lowerTechName = techName.toLowerCase();
  for (const [key, value] of Object.entries(techLogos)) {
    if (lowerTechName.includes(key.toLowerCase()) || key.toLowerCase().includes(lowerTechName)) {
      return `/images/about/skills/${value}`;
    }
  }

  // Default icon for unknown technologies
  return `/images/about/skills/default-tech.svg`;
};

// Component for skill list item with logo
const SkillListItem = ({ children }) => {
  const techName = children;
  const logoSrc = getTechLogo(techName);

  return (
    <li className="skill-list-item-with-logo">
      <img
        src={`${process.env.PUBLIC_URL}${logoSrc}`}
        alt={`${techName} logo`}
        className="skill-logo"
        onError={(e) => {
          e.target.src = `${process.env.PUBLIC_URL}/images/about/skills/default-tech.svg`;
        }}
      />
      <span>{children}</span>
    </li>
  );
};

const Home = () => {
  const heroImageRef = useRef(null);
  const heroContentRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);


  // Fetch skills data
  useEffect(() => {
    const fetchSkills = async () => {
      try {
        const response = await axios.get('/api/skills');
        setSkills(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching skills:', err);
        setLoading(false);
      }
    };

    fetchSkills();
  }, []);

  // Animation for hero section elements
  useEffect(() => {
    setIsVisible(true);
  }, []);


  // Parallax scrolling effect
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      if (heroImageRef.current) {
        heroImageRef.current.style.transform = `translateY(${scrollPosition * 0.2}px)`;
      }
      if (heroContentRef.current) {
        heroContentRef.current.style.transform = `translateY(${scrollPosition * 0.1}px)`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="home-page">
      <section className="hero-section">
        <div className="hero-bg">
          <div className="hero-particles"></div>
        </div>
        <Container fluid className="hero-container">
          <Row className="align-items-center">
            <Col lg={6} className={`hero-content text-left ${isVisible ? 'fade-in' : ''}`} ref={heroContentRef}>
              <div className="hero-intro">
                <div className="badge-container">
                  <span className="badge badge-animate">Software and AI Professional</span>
                </div>
                <div className="hero-headline">
                  <h1 className="hero-greeting">Hello, I'm</h1>
                  <h1 className="hero-name text-gradient">Rahul Bhoyar</h1>
                </div>
                <div className="hero-tagline-container">
                  <p className="hero-tagline">
                    Crafting <span className="highlight pulse">intelligent digital experiences</span> at the intersection of design and technology.
                  </p>
                </div>
              </div>
              <div className="hero-buttons">
                <Button as={Link} to="/experience" className="cta-button primary-cta">
                  <span className="cta-text">View Experience</span>
                  <span className="cta-icon"><FaArrowRight /></span>
                </Button>
                <Button as={Link} to="/projects" className="cta-button secondary-cta">
                  <span className="cta-text">Explore Projects</span>
                </Button>
              </div>
              <div className="social-links-hero">
                <a href="https://github.com/rahulbhoyar1995" target="_blank" rel="noopener noreferrer" className="social-link github" title="GitHub">
                  <FaGithub />
                </a>
                <a href="https://www.linkedin.com/in/rahul-bhoyar-mba-1a04a7215/" target="_blank" rel="noopener noreferrer" className="social-link linkedin" title="LinkedIn">
                  <FaLinkedin />
                </a>
              </div>
            </Col>
            <Col lg={6} className={`hero-image-col ${isVisible ? 'slide-in-right' : ''}`}>
              <div className="hero-image-container" ref={heroImageRef}>
                <img
                  src={`${process.env.PUBLIC_URL}/images/home/home_page.png`}
                  alt="Rahul Bhoyar"
                  className="hero-image"
                />
                <div className="hero-image-overlay"></div>
              </div>
            </Col>
          </Row>
          <div className="scroll-indicator">
            <FaAngleDown className="scroll-icon bounce" />
          </div>
        </Container>
      </section>





      <section className="section about-section">
        <Container>
          <h2 className="section-title text-center mb-5">About Me</h2>
          <Row className="align-items-start">
            <Col lg={6} className="mb-4 mb-lg-0">
              <div className="about-content text-left">
                <h3 className="mb-3 text-gradient">AI and Software Professional</h3>
                <p className="lead">
                  Results-driven AI &amp; Software Professional with 9+ years of experience designing and building scalable, intelligent systems at the intersection of Generative AI, Deep Learning, ML/NLP, and Full-Stack Engineering.
                </p>
                <p>
                  Currently, I work as an AI Engineer - Senior Specialist at Scope Ratings in Frankfurt, building intelligent AI systems for credit risk analysis, financial data intelligence, and automated rating workflows. I hold an MBA in Artificial Intelligence from the International University of Applied Sciences, Berlin, Germany.
                </p>
                <p>
                  My technical proficiency encompasses Python, FastAPI, Django, Flask, Agentic AI frameworks (LangChain, LangGraph, LangSmith, LangFuse, LlamaIndex, Ollama), cloud platforms (AWS, GCP, Microsoft Azure), and DevOps tools including Docker, Kubernetes, and Apache Kafka. I am also skilled in frontend technologies like React, JavaScript, and Streamlit.
                </p>
                <p>
                  Skilled in Agentic AI, RAG, Fine-Tuning LLMs, and Multi-Modal systems. Experienced in Distributed Systems Design, Cloud-Native Development, and end-to-end ML Pipeline optimization. Passionate about transforming complex ideas into production-grade AI solutions that drive innovation, automation, and measurable impact.
                </p>
                <div className="about-stats">
                  <div className="stat-item">
                    <span className="stat-number counter">9+</span>
                    <span className="stat-label">Years Experience</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number counter">75+</span>
                    <span className="stat-label">Projects Delivered</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-number counter">20+</span>
                    <span className="stat-label">Tech Stack Mastery</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <div className="about-card">
                <div className="about-card-header">
                  <div className="about-card-avatar-container">
                    <img
                      src={`${process.env.PUBLIC_URL}/images/about/profile/Rahul_Bhoyar_passport_photo.webp`}
                      alt="Rahul Bhoyar"
                      className="about-card-avatar-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="about-card-avatar" style={{display: 'none'}}>RB</div>
                  </div>
                  <div className="about-card-info">
                    <h3>Rahul Bhoyar</h3>
                    <p>AI and Software Professional</p>
                  </div>
                </div>
                <div className="about-card-body">
                  <div className="about-card-section text-left">
                    <h4>Core Technologies</h4>
                    <div className="tech-stack-icons">
                      <span className="tech-icon" title="Python">Py</span>
                      <span className="tech-icon" title="JavaScript">Js</span>
                      <span className="tech-icon" title="React">Re</span>
                      <span className="tech-icon" title="FastAPI">Fa</span>
                      <span className="tech-icon" title="Docker">Do</span>
                      <span className="tech-icon" title="TensorFlow">Tf</span>
                      <span className="tech-icon" title="PyTorch">Pt</span>
                      <span className="tech-icon" title="AWS">Aw</span>
                    </div>
                  </div>
                  <div className="about-card-section text-left">
                    <h4>Specializations</h4>
                    <p>Agentic AI, Generative AI, Deep Learning, Machine Learning, Data Science, Data Engineering, Backend and Frontend Development</p>
                  </div>
                  <div className="about-card-section text-left">
                    <h4>Languages</h4>
                    <p>English (C2), German (A2)</p>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <section className="section bg-gradient-dark skills-section">
        <Container>
          <h2 className="section-title text-center">Technical Skills</h2>
          <div className="skills-intro text-center mb-5">
            <p className="lead">
              My expertise spans across multiple domains, with a focus on full-stack development and AI technologies
            </p>
          </div>
          {!loading && (
            <>
              <Row>
                {skills.map((skill, index) => (
                  <Col md={6} key={index} className="mb-4">
                    <SkillBar skill={skill} />
                  </Col>
                ))}
              </Row>
              <div className="skills-categories mt-5">
                <Row>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-accent mb-3">🤖 Agentic AI and Generative AI</h4>
                      <ul className="skill-list">
                        <SkillListItem>LangGraph</SkillListItem>
                        <SkillListItem>LangChain</SkillListItem>
                        <SkillListItem>LangSmith</SkillListItem>
                        <SkillListItem>LangFuse</SkillListItem>
                        <SkillListItem>MCP</SkillListItem>
                        <SkillListItem>Ollama</SkillListItem>
                        <SkillListItem>Phidata</SkillListItem>
                        <SkillListItem>LlamaIndex</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-accent mb-3">🧠 ML / DL / NLP Stack</h4>
                      <ul className="skill-list">
                        <SkillListItem>HuggingFace</SkillListItem>
                        <SkillListItem>PyTorch</SkillListItem>
                        <SkillListItem>TensorFlow</SkillListItem>
                        <SkillListItem>Natural Language Processing</SkillListItem>
                        <SkillListItem>Computer Vision</SkillListItem>
                        <SkillListItem>MLOps & Model Deployment</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-secondary mb-3">⚙️ Backend Development</h4>
                      <ul className="skill-list">
                        <SkillListItem>Python</SkillListItem>
                        <SkillListItem>FastAPI</SkillListItem>
                        <SkillListItem>Django</SkillListItem>
                        <SkillListItem>Flask</SkillListItem>
                        <SkillListItem>RESTful API Design</SkillListItem>
                        <SkillListItem>Microservices Architecture</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-primary mb-3">🎨 Frontend Development</h4>
                      <ul className="skill-list">
                        <SkillListItem>React</SkillListItem>
                        <SkillListItem>JavaScript</SkillListItem>
                        <SkillListItem>HTML5</SkillListItem>
                        <SkillListItem>CSS3</SkillListItem>
                        <SkillListItem>Streamlit</SkillListItem>
                        <SkillListItem>TypeScript</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-secondary mb-3">🗄️ Databases</h4>
                      <ul className="skill-list">
                        <SkillListItem>SQL</SkillListItem>
                        <SkillListItem>MySQL</SkillListItem>
                        <SkillListItem>MongoDB</SkillListItem>
                        <SkillListItem>Redis</SkillListItem>
                        <SkillListItem>Vector DBs</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                  <Col md={6} className="mb-4">
                    <div className="skill-category-card text-left">
                      <h4 className="text-primary mb-3">☁️ Cloud, DevOps & Streaming</h4>
                      <ul className="skill-list">
                        <SkillListItem>AWS</SkillListItem>
                        <SkillListItem>Docker</SkillListItem>
                        <SkillListItem>Kubernetes</SkillListItem>
                        <SkillListItem>Apache Kafka</SkillListItem>
                        <SkillListItem>Prometheus</SkillListItem>
                        <SkillListItem>Grafana</SkillListItem>
                      </ul>
                    </div>
                  </Col>
                </Row>
              </div>
            </>
          )}
        </Container>
      </section>
    </div>
  );
};

export default Home;
